import axios from 'axios';

const API_URL = 'https://dailyreportsapi.winggs.in/api';
const BASE_URL = 'https://dailyreportsapi.winggs.in';
// const API_URL = 'http://localhost:5000/api'; // Use this for local development
// const BASE_URL = 'http://localhost:5000'; // Base URL without the /api path for file access

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'x-auth-token': token } : {};
};

const getProjectFiles = async (projectId) => {
  const response = await axios.get(`${API_URL}/projects/${projectId}/files`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

const uploadProjectFile = async (projectId, file) => {
  const formData = new FormData();
  formData.append('projectFile', file);

  const response = await axios.post(`${API_URL}/projects/${projectId}/files`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

const deleteProjectFile = async (fileId) => {
  const response = await axios.delete(`${API_URL}/files/${fileId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Helper function to properly construct file URLs
const getFileUrl = (filePath) => {
  if (!filePath) return '';
  
  // If it's already a full URL (e.g., cloud storage URL), return as is
  if (filePath.startsWith('http')) {
    return filePath;
  }
  
  // Use the file path as stored in database - works for both local and production
  return `${BASE_URL}/${filePath}`;
};

const getFileMetadata = async (fileId) => {
  const response = await axios.get(`${API_URL}/files/${fileId}/metadata`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

const getFileDownloadUrl = (fileId) => {
  return `${API_URL}/files/${fileId}/download`;
};

// Method to handle file download with authentication
const downloadFile = async (fileId) => {
  try {
    // First, get the file metadata to know what kind of file we're dealing with
    const metadataResponse = await axios.get(`${API_URL}/files/${fileId}/metadata`, {
      headers: getAuthHeaders()
    });
    
    const fileMetadata = metadataResponse.data;
    console.log('File metadata for download:', fileMetadata);
    
    // Use the correct download URL - only use the file ID, not the path
    const downloadUrl = `${API_URL}/files/${fileId}/download`;
    
    // Create a hidden form for POST request with authentication token
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = downloadUrl;
    form.target = '_blank'; // Open in new tab to trigger download
    form.style.display = 'none';
    
    // Add auth token as hidden field
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token';
    tokenInput.value = localStorage.getItem('token') || '';
    form.appendChild(tokenInput);
    
    // Submit the form
    document.body.appendChild(form);
    form.submit();
    setTimeout(() => {
      document.body.removeChild(form);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

const getFilePreviewUrl = (fileId) => {
  return `${API_URL}/files/${fileId}/preview`;
};

// Method to handle file preview with authentication
const previewFile = async (fileId, fileName) => {
  try {
    // First, get the file metadata to know what kind of file we're dealing with
    const metadataResponse = await axios.get(`${API_URL}/files/${fileId}/metadata`, {
      headers: getAuthHeaders()
    });
    
    const fileMetadata = metadataResponse.data;
    console.log('File metadata for preview:', fileMetadata);
    
    // Use the correct preview URL - only use the file ID, not the path
    const previewUrl = `${API_URL}/files/${fileId}/preview`;
    
    // Create a hidden form for POST request with authentication token
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = previewUrl;
    form.target = '_blank'; // Open in new tab for preview
    form.style.display = 'none';
    
    // Add auth token as hidden field
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token';
    tokenInput.value = localStorage.getItem('token') || '';
    form.appendChild(tokenInput);
    
    // Add preview flag
    const previewInput = document.createElement('input');
    previewInput.type = 'hidden';
    previewInput.name = 'preview';
    previewInput.value = 'true';
    form.appendChild(previewInput);
    
    // Submit the form
    document.body.appendChild(form);
    form.submit();
    setTimeout(() => {
      document.body.removeChild(form);
    }, 100);
    
    return true;
  } catch (error) {
    console.error('Preview error:', error);
    throw error;
  }
};

// Function to determine if a file is viewable based on its extension
const isFileViewable = (fileName) => {
  if (!fileName) return false;
  
  const extension = fileName.split('.').pop().toLowerCase();
  const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'svg', 'txt', 'csv', 'md', 'html', 'htm'];
  
  return viewableExtensions.includes(extension);
};

// Method to handle unblurring a file (for admin only)
const unblurFile = async (fileId) => {
  const response = await axios.put(`${API_URL}/files/${fileId}/unblur`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

const fileService = {
  getProjectFiles,
  uploadProjectFile,
  deleteProjectFile,
  getFileUrl,
  getFileMetadata,
  getFileDownloadUrl,
  getFilePreviewUrl,
  downloadFile,
  previewFile,
  isFileViewable,
  unblurFile,
};

export default fileService;
